import { useState, useEffect } from 'react';
import OpenAI from 'openai';
import './App.css';
import LoadingAnimation from './LoadingAnimation';
import SearchHistory from './SearchHistory';
import StoryConfigModal from './StoryConfigModal';
import StoryLoadingAnimation from './StoryLoadingAnimation';
import RecentStories from './RecentStories';
import SearchToggle from './SearchToggle'; 
import api from './services/api';  // Add this import

// StoryModal component remains unchanged
const StoryModal = ({ story, onClose }) => {
  const handleBackdropClick = (e) => {
    if (e.target.className === 'modal-backdrop') {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container">
        <div className="modal-header">
          <h2>Generated Story</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-content">
          <div dangerouslySetInnerHTML={{ __html: story }} />
        </div>
      </div>
    </div>
  );
};

function App() {
  const [scenario, setScenario] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [popularScenarios, setPopularScenarios] = useState([]);
  const [storyLoading, setStoryLoading] = useState(false);
  const [story, setStory] = useState(null);
  const [showStoryModal, setShowStoryModal] = useState(false);
  const [showStoryConfig, setShowStoryConfig] = useState(false);
  const [recentStories, setRecentStories] = useState([]);

  // Modified to use MongoDB
  const saveStory = async (storyContent, config) => {
    const timestamp = new Date().toISOString();
    
    // Extract title from HTML content and clean up ticker duplicates
    let title = 'Untitled Story';
    const titleMatch = storyContent.match(/<h1[^>]*>(.*?)<\/h1>/);
    if (titleMatch) {
      // Clean up the title by:
      // 1. Remove HTML tags
      // 2. Remove duplicate ticker symbols like (HD) (HD)
      // 3. Keep only the first instance of a ticker symbol
      title = titleMatch[1]
        .replace(/<[^>]+>/g, '')  // Remove HTML tags
        .replace(/\(([A-Z]+)\)\s*\(\1\)/g, '($1)')  // Remove duplicate tickers
        .trim();
    }
    
    const newStory = {
      content: storyContent
        .replace(/\(([A-Z]+)\)\s*\(\1\)/g, '($1)'), // Clean duplicates in content too
      title: title,
      timestamp: timestamp,
      views: 1,
      lastViewed: timestamp,
      scenario: scenario,
      results: results,
      ...config
    };
    
    try {
      await api.saveStory(newStory);
      const updatedStories = await api.getStories();
      setRecentStories(updatedStories);
    } catch (err) {
      console.error('Error saving story:', err);
      setError('Failed to save story: ' + err.message);
    }
  };

  const generateStory = async (config) => {
    setShowStoryConfig(false);
    await new Promise(resolve => setTimeout(resolve, 100));
    setStoryLoading(true);
    
    try {
      const openai = new OpenAI({
        apiKey: process.env.REACT_APP_OPENAI_API_KEY,
        dangerouslyAllowBrowser: true
      });

      const completion = await openai.chat.completions.create({
        messages: [
          { 
            role: "system",
            content: `You are writing just like ${config.authorStyle}. You write ${config.slant.toLowerCase()} analysis about market scenarios.${config.notes ? ` Your editor has given you these additional notes: ${config.notes}` : ''}`
            
          },
          { 
            role: "user", 
            content: `Write a ${config.length} word story in the style of ${config.authorStyle} about this possible scenario: "${scenario}"

Here are some stocks that benefit: ${results.beneficial.map(stock => `${stock.name} (${stock.ticker})`).join(', ')}

And some that are negatively impacted: ${results.negative.map(stock => `${stock.name} (${stock.ticker})`).join(', ')}

Be bold and confident. Create a clickbait-style headline but make it unique and different each time.
The story should be engaging and well-written, focusing on the most interesting aspects.
You don't need to mention all stocks - just make it the best possible read.
Always put the ticker symbol after the company name on first mention like "Apple Inc (AAPL)" and bold the ticker. IMPORTANT: Only include the ticker symbol ONCE after the company name, do not repeat it.
Format the response in HTML for easy display and copy/paste. IMPORTANT TICKER RULES:
- First mention of a company: "Company Name (TICKER)"
- NEVER repeat the ticker immediately after itself
- After first mention, use either company name OR ticker alone
- NO duplicate tickers like "(MSFT) MSFT" or "(MSFT) (MSFT)"Use short paragraphs. Include headings to separate the content. Include 3 alternate titles/subject lines at the bottom of the story spaced one by one, clearly marked. Include 5 tweets to promote the story with no hashtags and spaced one by one`          }
        ],
        model: "gpt-4.5-preview",
      });

      const generatedContent = completion.choices[0].message.content;
      setStory(generatedContent);
      await saveStory(generatedContent, config);
      setShowStoryModal(true);
      
    } catch (err) {
      setError(`Error generating story: ${err.message}`);
      console.error('Story generation error:', err);
    } finally {
      setStoryLoading(false);
    }
  };

  // Modified to use MongoDB
  const saveScenario = async (scenarioText, results) => {
    const timestamp = new Date().toISOString();
    const newScenario = {
      text: scenarioText,
      timestamp: timestamp,
      views: 1,
      lastViewed: timestamp,
      results: results
    };
    
    try {
      await api.saveScenario(newScenario);
      const scenarios = await api.getScenarios();
      setPopularScenarios(scenarios);
    } catch (err) {
      console.error('Error saving scenario:', err);
      setError('Failed to save scenario: ' + err.message);
    }
  };

  // Modified to use MongoDB
  const getPopularScenarios = async (period = 'day') => {
    try {
      const scenarios = await api.getScenarios();
      const now = new Date();
      
      const filtered = scenarios.filter(scenario => {
        const scenarioDate = new Date(scenario.lastViewed);
        switch(period) {
          case 'day':
            return now.getTime() - scenarioDate.getTime() < 24 * 60 * 60 * 1000;
          case 'week':
            return now.getTime() - scenarioDate.getTime() < 7 * 24 * 60 * 60 * 1000;
          default:
            return true;
        }
      });
      
      return filtered.sort((a, b) => b.views - a.views).slice(0, 10);
    } catch (err) {
      console.error('Error fetching scenarios:', err);
      return [];
    }
  };

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [stories, history] = await Promise.all([
          api.getStories(),
          api.getSearchHistory()
        ]);
        setRecentStories(stories);
        setSearchHistory(history);
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError('Failed to load initial data: ' + err.message);
      }
    };

    loadInitialData();
  }, []);

  // Modified to use MongoDB
  const handleAnalyze = async (scenarioText = scenario) => {
    setLoading(true);
    setError(null);
  
    try {
      // Check if scenario exists in database
      const scenarios = await api.getScenarios();
      const existingScenario = scenarios.find(s => s.text === scenarioText);
      
      if (existingScenario && existingScenario.results) {
        setResults(existingScenario.results);
        await saveScenario(scenarioText, existingScenario.results);
        
        // Update search history
        await api.saveSearchHistory({ searchTerm: scenarioText });
        const updatedHistory = await api.getSearchHistory();
        setSearchHistory(updatedHistory);
        
        setLoading(false);
        return;
      }
  
      const openai = new OpenAI({
        apiKey: process.env.REACT_APP_OPENAI_API_KEY,
        dangerouslyAllowBrowser: true
      });
  
      const completion = await openai.chat.completions.create({
        messages: [
          { 
            role: "system",
            content: "You are a financial analyst who specializes in analyzing how scenarios affect stock prices. You provide clear, concise analyses with detailed rationales."
          },
          { 
            role: "user", 
            content: `Given this future scenario: "${scenarioText}"
            Please analyze how this scenario might affect different publicly traded companies.
            Provide two lists:
            1. 7 U.S. public companies that could benefit from this scenario
            2. 7 U.S. public companies that could be negatively impacted by this scenario
            
            For each company, provide:
            - Ticker symbol
            - Company name
            - A brief explanation of why (1-2 sentences)
            
            Format your response exactly like this, including the curly braces:
            {
              "beneficial": [
                {"ticker": "XYZ", "name": "Company Name", "summary": "Reason for impact"}
              ],
              "negative": [
                {"ticker": "ABC", "name": "Company Name", "summary": "Reason for impact"}
              ]
            }` 
          }
        ],
        model: "gpt-4.5-preview",
      });
  
      const response = JSON.parse(completion.choices[0].message.content);
      setResults(response);
      await saveScenario(scenarioText, response);
      
      // Update search history
      await api.saveSearchHistory({ searchTerm: scenarioText });
      const updatedHistory = await api.getSearchHistory();
      setSearchHistory(updatedHistory);
    } catch (err) {
      setError(`Error: ${err.message}`);
      console.error('Full error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      {loading && <LoadingAnimation />}
      {storyLoading && <StoryLoadingAnimation results={results} />}
      
      <header>
        <h1>Scenario Navigator</h1>
      </header>
    
      <main>
        <section className="grid-layout">
          <div className="input-container">
            <textarea
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
              placeholder="Describe any potential future scenario..."
              rows={4}
            />
            <button 
              onClick={() => handleAnalyze()} 
              className="analyze-button"
              disabled={loading || !scenario.trim()}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  Analyzing...
                </>
              ) : (
                'Analyze Scenario'
              )}
            </button>
            {error && <div className="error-message">{error}</div>}
          </div>
          
          <SearchToggle 
  searchHistory={searchHistory || []}
  popularScenarios={popularScenarios || []}
  onScenarioSelect={(text) => {
    setScenario(text);
    handleAnalyze(text);
  }}
/>
        </section>

        {results && (
          <section className="results-section">
            <div className="results-header">
              <h2>Analysis Results</h2>
              <button 
                className="write-story-button"
                onClick={() => setShowStoryConfig(true)}
                disabled={storyLoading}
              >
                {storyLoading ? 'Writing...' : 'Write Story'}
              </button>
            </div>
            
            <div className="table-container">
              <h3>Stocks benefitting from "{scenario}"</h3>
              <table>
                <thead>
                  <tr>
                    <th>TICKER</th>
                    <th>COMPANY NAME</th>
                    <th>SUMMARY</th>
                  </tr>
                </thead>
                <tbody>
                  {results.beneficial.map((stock, index) => (
                    <tr key={index}>
                      <td>{stock.ticker}</td>
                      <td>{stock.name}</td>
                      <td>{stock.summary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="table-container">
              <h3>Stocks negatively impacted by "{scenario}"</h3>
              <table>
                <thead>
                  <tr>
                    <th>TICKER</th>
                    <th>COMPANY NAME</th>
                    <th>SUMMARY</th>
                  </tr>
                </thead>
                <tbody>
                  {results.negative.map((stock, index) => (
                    <tr key={index}>
                      <td>{stock.ticker}</td>
                      <td>{stock.name}</td>
                      <td>{stock.summary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {recentStories.length > 0 && (
          <RecentStories
            stories={recentStories}
            onStorySelect={(story) => {
              const updatedStories = recentStories.map(s => {
                if (s.timestamp === story.timestamp) {
                  return { ...s, views: s.views + 1 };
                }
                return s;
              });
              setRecentStories(updatedStories);
              
              // Save to MongoDB instead of localStorage
              api.updateStory(story.timestamp, { views: story.views + 1 })
                .catch(err => console.error('Error updating story views:', err));
              
              setStory(story.content);
              setShowStoryModal(true);
            }}
          />
        )}

        <StoryConfigModal 
          show={showStoryConfig}
          results={results}
          onSubmit={generateStory}
          onClose={() => setShowStoryConfig(false)}
        />

        {showStoryModal && story && (
          <StoryModal 
            story={story} 
            onClose={() => setShowStoryModal(false)} 
          />
        )}
      </main>
    </div>
  );
}

export default App;